// How an alteration's fit was captured, for components/PerfectFit.jsx.
//
// A menu alteration carries `info_mode`, the list of ways the fit can be
// taken for that service. The Fit details screen turns it into buttons.
//
// Two kinds of service have nothing to put on that screen: a custom
// write-in, which has no `info_mode` field at all, and a menu service whose
// `info_mode` is empty. Before 31 Oct 2026 only the empty list skipped the
// screen, because the check was `info_mode?.length == 0` and a missing
// field gives `undefined`, which is not `0`. A custom alteration therefore
// landed on "Fit details", was asked how the fit was captured, and was
// offered no way to answer (Calley, 31 Oct 2026, a $220 custom dress
// alteration on prod).
//
// Pure: no React, so node:test can load it.

/** The capture methods, in the order the screen shows them. `infoMode` is
 *  the value stored on the service; `type` is what the order records. */
export const METHODS = [
  { type: "Measure", infoMode: "Measure Items", label: "Measure" },
  { type: "Pin Items", infoMode: "Pin Items", label: "Pin Items" },
  { type: "Match Items", infoMode: "Match Items", label: "Match Items" },
];

/** A service's `info_mode` as a list, whatever shape it arrived in. A
 *  missing field and an empty list both mean "nothing to ask". */
export const infoModesOf = (service) =>
  Array.isArray(service?.info_mode) ? service.info_mode : [];

/** The methods this service actually offers, in screen order. Empty means
 *  the screen has no question to ask and should be skipped. */
export const fitMethodsFor = (service) => {
  const modes = infoModesOf(service);
  return METHODS.filter((m) => modes.includes(m.infoMode));
};

/** The service this screen is about: the one just added, matched by name
 *  against either the route state or what the service screen stored.
 *
 *  Only a name with something in it can match. The old inline version used
 *  `==`, so a missing route name and a service with no name matched each
 *  other (`undefined == null`) and the screen could land on the wrong line. */
export const serviceForFit = (services, selectedOption, storedOption) => {
  const wanted = [selectedOption, storedOption].filter(
    (name) => typeof name === "string" && name.trim() !== "",
  );
  if (wanted.length === 0) return null;
  return (
    (services || []).find(
      (s) => typeof s?.name === "string" && wanted.includes(s.name),
    ) || null
  );
};
